import React, { useMemo } from "react";
import { Filter, X } from "react-feather";
import { Card } from "../../../shared/Card/Card";
import { PrimaryButton } from "../../../shared/Buttons/Buttons";
import "./interviewLogFilter.scss";

interface InterviewLogFilterProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedInterviewUser: string | undefined;
  setSelectedInterviewUser: (user: string | undefined) => void;
  dateFrom: Date | undefined;
  setDateFrom: (date: Date | undefined) => void;
  dateTo: Date | undefined;
  setDateTo: (date: Date | undefined) => void;
  clearAllFilters: () => void;
  allInterviewLogs: any[];
}

const InterviewLogFilter: React.FC<InterviewLogFilterProps> = ({
  searchTerm,
  setSearchTerm,
  selectedInterviewUser,
  setSelectedInterviewUser,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  clearAllFilters,
  allInterviewLogs,
}) => {
  // Get unique users from the interview logs
  const uniqueUsers = useMemo(() => {
    const users = new Map();
    allInterviewLogs.forEach((interview) => {
      if (interview.user?.name) {
        // Use userId as the key to ensure uniqueness
        const userId = interview.userId || interview.user?.id;
        if (userId) {
          // If user already exists, update with more complete info
          const existingUser = users.get(userId);
          if (!existingUser || !existingUser.email) {
            users.set(userId, {
              id: userId,
              name: interview.user.name,
              email: interview.user.email || "",
            });
          }
        }
      }
    });
    const uniqueUsersArray = Array.from(users.values()).sort((a: any, b: any) =>
      a.name.localeCompare(b.name)
    );

    return uniqueUsersArray;
  }, [allInterviewLogs]);

  const formatDate = (date: Date) => {
    return date.toISOString().split("T")[0];
  };

  return (
    <Card>
      <div className="interview-log-filter">
        <div className="filter-header">
          <div className="filter-title">
            <Filter className="filter-icon" />
            <h3>Filters</h3>
          </div>
          <PrimaryButton
            text="Clear All"
            type="button"
            onClick={clearAllFilters}
            size="sm"
          />
        </div>

        <div className="filter-grid">
          {/* Search */}
          <div className="filter-field">
            <label htmlFor="search-input" className="filter-label">
              Search
              <input
                id="search-input"
                type="text"
                placeholder="Search interviews..."
                className="filter-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </label>
          </div>

          {/* User Filter */}
          <div className="filter-field">
            <label htmlFor="user-select" className="filter-label">
              User
              <select
                id="user-select"
                className="filter-select"
                value={selectedInterviewUser}
                onChange={(e) => setSelectedInterviewUser(e.target.value)}
              >
                <option value="all">All Users</option>
                {uniqueUsers.map((user: any) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {/* Date From */}
          <div className="filter-field">
            <label htmlFor="date-from-input" className="filter-label">
              Date From
              <input
                id="date-from-input"
                type="date"
                className="filter-input"
                value={dateFrom ? formatDate(dateFrom) : ""}
                onChange={(e) =>
                  setDateFrom(
                    e.target.value ? new Date(e.target.value) : undefined
                  )
                }
              />
            </label>
          </div>

          {/* Date To */}
          <div className="filter-field">
            <label htmlFor="date-to-input" className="filter-label">
              Date To
              <input
                id="date-to-input"
                type="date"
                className="filter-input"
                value={dateTo ? formatDate(dateTo) : ""}
                onChange={(e) =>
                  setDateTo(
                    e.target.value ? new Date(e.target.value) : undefined
                  )
                }
              />
            </label>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default InterviewLogFilter;
